/**
 * Connected Evacuation Orders affordance (#206) — the community/incident → evac-
 * order half of the cross-link. Rendered inside a community panel or an incident's
 * management section; it looks up the evacuation orders whose polygon covers the
 * entity (community footprint, or any community linked to the incident) and lists
 * them with a contextual jump that opens the order panel + flies the COP.
 *
 * Per design-council PART IX it renders NOTHING until at least one order resolves —
 * no COP clutter on the (common) case of an entity under no active order. Self-
 * contained: navigation rides the same-window `selectOnCop` bridge (#185), so no
 * callback threading through the panel tree.
 */
import { useEffect, useState } from 'react';
import type { EvacOrderRef } from '@tsr/shared-types';
import { fetchCommunityEvacOrders, fetchIncidentEvacOrders } from './api';
import { selectOnCop } from './selectionBridge';
import { sortEvacOrders, hasEvacOrderLinks, evacOrderToSelection } from './lib/evacLink';
import { evacuationStyle } from './styles/evacuationStyles';

export type EvacOrderSource = { kind: 'community' | 'incident'; id: string };

function orderLabel(o: EvacOrderRef): string {
  return o.name || (o.severity === 'order' ? 'Evacuation order' : o.severity === 'alert' ? 'Evacuation alert' : 'Evacuation area');
}

export function ConnectedEvacOrders({ source }: { source: EvacOrderSource }): JSX.Element | null {
  const [orders, setOrders] = useState<EvacOrderRef[]>([]);

  useEffect(() => {
    const ctl = new AbortController();
    const fetcher = source.kind === 'incident' ? fetchIncidentEvacOrders : fetchCommunityEvacOrders;
    void fetcher(source.id, ctl.signal)
      .then((r) => setOrders(sortEvacOrders(r.orders)))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [source.kind, source.id]);

  // Contextual gate: no covering orders → render nothing.
  if (!hasEvacOrderLinks(orders)) return null;

  return (
    <section data-testid="connected-evac-orders" className="space-y-1.5">
      <h3 className="text-xs uppercase tracking-wider text-slate-400">
        Evacuation orders{orders.length > 1 ? ` (${orders.length})` : ''}
      </h3>
      <ul className="space-y-1">
        {orders.map((o) => {
          const style = evacuationStyle(o.severity);
          return (
            <li key={o.id}>
              <button
                type="button"
                data-testid="connected-evac-order-btn"
                onClick={() => selectOnCop(evacOrderToSelection(o))}
                className="flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left text-[12px] hover:brightness-125"
                style={{ borderColor: `${style.color}80`, backgroundColor: `${style.color}1f`, color: style.color }}
              >
                <span aria-hidden className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: style.color }} />
                <span className="min-w-0 flex-1 truncate">
                  {orderLabel(o)}
                  <span className="opacity-80">
                    {' · '}
                    {style.label}
                    {o.population != null ? ` · ${o.population.toLocaleString()} ppl` : ''}
                  </span>
                </span>
                <span aria-hidden className="shrink-0">→</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
